import { useState, useEffect } from 'react';
import { Menu as MenuIcon } from 'lucide-react';
import { Menu } from './Menu';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useSaldo } from '../contexts/SaldoContext';
import { SaldoBadge } from './SaldoBadge';
import { WhatsAppSuporte } from './WhatsAppSuporte';
import { AvisoTelefonesSuporte } from './AvisoTelefonesSuporte';
import { useSuporteWhatsApp } from '../hooks/useSuporteWhatsApp';
import { useAtualizacao } from '../hooks/useAtualizacao';
import { useFontCache } from '../hooks/useFontCache';
import logoGraviAguas from '../assets/images/logo-gravi-aguas.svg';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { error: saldoError } = useSaldo();
  const { telefone: telefoneSuporte, mensagem: mensagemSuporte, avisoTelefones } = useSuporteWhatsApp();
  const { clearFontCache } = useFontCache();
  const { atualizarFontes } = useAtualizacao({ 
    clearFontCache: async () => {
      // Limpa o cache e recarrega as fontes automaticamente
      await clearFontCache({ 
        reloadFonts: true,
        onProgress: () => {
          // A função onProgress é chamada durante o processo de recarregamento das fontes.
          // Ela recebe um parâmetro 'progress' que indica a porcentagem de conclusão
          // do recarregamento. Isso pode ser usado para fornecer feedback visual ao usuário,
          // como uma barra de progresso ou mensagens de status.
        }
      });
    }
  });

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };    

  // Verifica atualizações quando o componente é montado
  useEffect(() => {
    const checkAtualizacao = async () => {
      await atualizarFontes();
    };
    
    checkAtualizacao();
  }, []);

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Aviso de sessão expirada */}
      {saldoError && saldoError.includes('Sessão expirada') && (
        <div className="bg-red-600 text-white px-4 py-2 text-center text-sm font-medium">
          ⚠️ {saldoError}
        </div>
      )}
      
      <header className="w-full bg-white shadow-sm relative">
        <div className="px-6 py-4 flex items-center">
          <button 
            className="p-2 hover:bg-gray-100 rounded-full mr-4"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <MenuIcon className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex-1">
            <Link to="/home">
              {user && ((user.tipo === 'usuario' || user.tipo === 'admin') && (!user.id_revenda || user.id_revenda === '')) ? 
                <img src={logoGraviAguas} alt="Gravi Águas" className="h-12" /> : 'Painel'}
            </Link>
          </h1>
          <div className="flex gap-2 items-center">
            {/* No mobile, WhatsApp e saldo ficam dentro do menu hamburguer */}
            {telefoneSuporte && avisoTelefones && (
              <AvisoTelefonesSuporte className="hidden sm:flex rounded-lg px-3 py-1 text-xs max-w-[360px]" />
            )}
            {telefoneSuporte && (
              <WhatsAppSuporte
                telefone={telefoneSuporte}
                mensagem={mensagemSuporte}
                className="hidden sm:flex rounded-full px-4 py-1 text-base"
              />
            )}
            <SaldoBadge className="hidden sm:block rounded-full px-4 py-1" />
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium">
              {user?.nome?.[0] || 'U'}
            </div>
          </div>
        </div>
        <Menu 
          isOpen={isMenuOpen} 
          onClose={() => setIsMenuOpen(false)}
          onLogout={handleLogout}
        />
      </header>

      <main className="px-6 py-8">
        {children}
      </main>      
    </div>
  );
} 